import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';

import { LoadingSpinner } from 'brand-guidelines/Loading-styled-components';
import { requestUserAndPatient } from 'connect/actions';
import { PatientV4Login } from 'connect/saga/handlers/user/patient-v4-login';
import { selectDataLoadingUser, selectDataPatient } from 'connect/selectors';
import { useCurrentCheckoutStep } from 'pages/checkout/hooks/checkoutStepHooks';

import { stepComponents } from 'pages/checkout/steps/stepComponents';
import { CheckoutStepDefinition } from 'pages/checkout/steps/types';
import { usePrevious } from 'utils/customHooks/usePrevious';
import { useLogger } from 'utils/Logger';

export const CurrentStep = () => {
  const dispatch = useDispatch();
  const { url: rawUrl, path } = useRouteMatch();
  const url = useMemo(
    // Remove trailing slash
    () => rawUrl.replace(/\/$/, ''),
    [rawUrl],
  );

  const logger = useLogger();
  const [isLoading, setLoading] = useState(true);

  const { currentStep, applicableStepDefinitions } = useCurrentCheckoutStep();
  const previousStep = usePrevious<CheckoutStepDefinition | null>(currentStep);
  const isLoadingLogin = useSelector(selectDataLoadingUser);
  const previousIsLoadingLogin = usePrevious(isLoadingLogin);

  const patient = useSelector(selectDataPatient);

  useEffect(() => {
    if (previousStep?.id !== currentStep?.id || !patient?.id) {
      // Makes sure the user and patient are loaded when a step changes
      dispatch(requestUserAndPatient());

      // Attempt to login to the patient v4 app
      dispatch(PatientV4Login.createAction());
    }
  }, [dispatch, patient?.id, currentStep?.id, previousStep?.id]);

  useEffect(() => {
    // Only setloading to false when the login was previously loading
    // And is no longer loading. So we wait for the login to start loading.
    // This prevents certain steps from being marked as inapplicable
    // before the user has loaded.
    // If we didn't do this, the user would always be redirected to the
    // first step when entering the checkout directly via URL.
    if (previousIsLoadingLogin && !isLoadingLogin) {
      setLoading(false);
    }
  }, [isLoadingLogin, previousIsLoadingLogin]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Switch>
      {applicableStepDefinitions.map((stepDefinition) => {
        const StepComponent = stepComponents[stepDefinition.id];

        // Should only happen in development
        if (!StepComponent) {
          logger.error('Step component not found for step', { stepDefinition });
          return null;
        }

        return (
          <Route key={stepDefinition.id} path={`${path}/${stepDefinition.path}`}>
            <StepComponent />
          </Route>
        );
      })}

      {/* Go to account step if step path is not given (/results/:clinicId/checkout) */}
      <Route exact path={[path, `${path}/`]}>
        <Redirect to={`${url}/${applicableStepDefinitions.at(0)!.path}`} />
      </Route>

      {/* Redirect to last applicable step if step path does not match an applicable step route */}
      <Route>
        <Redirect to={`${url}/${applicableStepDefinitions.at(-1)!.path}`} />
      </Route>
    </Switch>
  );
};
